import { ImmunizationRecord } from "../../models/Interfaces";
import db from "../../database/database";
import React, { useState, useEffect, forwardRef } from "react";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { useTranslation } from "react-i18next";
import "../../components/patientCard.css";
import ReportCard from "../../components/ReportCard";
import { getReportForState } from "../../utils/reportUtils";
import getIzProvider from "../../models/providers/IzProviders";
import { reportTypeFromString } from "../../apiClient";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { arrayBufferToBase64 } from "../../utils/reportUtils";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import {
  ModalV2,
  ModalV2BackButton,
  ModalV2Body,
  ModalV2Header,
} from "../../components/modals/ModalV2";

interface Report {
  contents: string;
  reportType: string;
  reportTitle: string;
  loading: boolean;
}

interface ReportListModalProps {
  record: ImmunizationRecord | null;
}

export const ReportListModal = forwardRef<HTMLDialogElement, ReportListModalProps>(
  function ReportListModal({ record }, ref) {
    const { t } = useTranslation();
    const [showErrorModal, setShowErrorModal] = useState<boolean | string>();
    const [reports, setReports] = useState<Map<number, Report>>(new Map());
    const providerKey = record?.izProviderKey;

    let izProvider;
    if (providerKey) {
      izProvider = getIzProvider(providerKey, t);
    }
    const reportTitles = izProvider?.reportTitles;

    const getReportContents = async (id: string): Promise<string> => {
      const reportResponse = await db().getItem(id);
      return typeof reportResponse === "string" && reportResponse.length > 0
        ? (reportResponse as string)
        : "";
    };

    const getReportId = (index: number) => {
      if (!record) return "";
      const reportType = getReportForState(providerKey!, index);
      const convertedReportType = reportTypeFromString(reportType);
      return record.patientId + "__" + record.searchUid + "__" + convertedReportType;
    };

    const initializeReports = async () => {
      if (!reportTitles || !record) return;

      let newReports = new Map<number, Report>();
      await Promise.all(
        reportTitles.map(async (title, index) => {
          const contents = await getReportContents(getReportId(index));
          newReports.set(index, {
            contents,
            reportType: getReportForState(providerKey!, index),
            reportTitle: title,
            loading: false,
          });
        })
      );

      setReports(newReports);
    };

    // Reset reports when record changes
    useEffect(() => {
      if (record) {
        setReports(new Map());
        initializeReports();
      }
    }, [record?.uid, reportTitles?.join(",")]);

    const getReport = async (index: number) => {
      if (!record) return;
      const report = reports.get(index);
      if (!report) return;

      report.loading = true;
      setReports(new Map(reports.set(index, report)));

      try {
        const response = await getAPIClient().getRecordReport(
          record.searchUid,
          reportTypeFromString(report.reportType),
          record.izProviderKey
        );

        const buffer = arrayBufferToBase64(response);
        if (buffer.length > 0) {
          report.contents = "data:application/pdf;base64," + buffer;
          setReports(new Map(reports.set(index, report)));
          await db().setItem(getReportId(index), "data:application/pdf;base64," + buffer);
        }
      } catch (e: unknown) {
        if (e instanceof DocketAPIError) {
          setShowErrorModal(e.message);
        }
      } finally {
        report.loading = false;
        setReports(new Map(reports.set(index, report)));
      }
    };

    const hardRefresh = async () => {
      const currReports = Array.from(reports.values());
      currReports.forEach((report, index) => {
        if (report.contents.length === 0) return;

        report.loading = true;
        setReports(new Map(reports.set(index, report)));
      });
      await getAPIClient().refreshRecords();
      await fetchRecordsUntilUpdated();
      currReports.forEach(async (report, index) => {
        if (report.contents.length === 0) return;

        // getReport handles loading state
        await getReport(index);
      });
    };

    const reportCards = () => {
      return Array.from(reports.entries()).map(([key, report]) => (
        <div key={key}>
          <ReportCard
            reportContents={report.contents}
            reportTitle={report.reportTitle}
            onRefresh={hardRefresh}
            loading={report.loading}
            isMiicRecord={providerKey === "mn"}
            onDownload={() => {
              getReport(key);
            }}
          />
        </div>
      ));
    };

    const noPatient = () => {
      return (
        <div className="mb-4" key="0">
          {t("immunizations.reports_no_patient")}
        </div>
      );
    };

    return (
      <ModalV2 modalBoxClassName="bg-base-100 max-w-3xl" ref={ref}>
        <div>
          <ModalV2Header className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <ModalV2BackButton />
              <p className="font-semibold text-xl">
                {/* TODO */}
                Immunization Reports
              </p>
            </div>
          </ModalV2Header>
        </div>
        <ModalV2Body className="bg-base-100 pt-2">
          {showErrorModal && (
            <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
          )}
          <div className="flex flex-col gap-4 p-4">{record ? reportCards() : noPatient()}</div>
        </ModalV2Body>
      </ModalV2>
    );
  }
);

export default ReportListModal;
